import * as apply from 'fp-ts/Apply';
import * as array from 'fp-ts/Array';
import * as chainable from 'fp-ts/Chain';
import * as e from 'fp-ts/Either';
import * as eT from 'fp-ts/EitherT';
import * as fe from 'fp-ts/FromEither';
import { constant, flow, identity, pipe } from 'fp-ts/function';
import * as o from 'fp-ts/Option';
import { sign } from 'fp-ts/Ordering';
import * as remote from './remote.js';
/**
 * @category Instances
 */
export const URI = 'RemoteEither';
/**
 * @category Constructors
 */
export const initial = remote.initial;
/**
 * @category Constructors
 */
export const constInitial = constant(initial);
/**
 * @category Constructors
 */
export const pending = remote.pending;
/**
 * @category Constructors
 */
export const constPending = constant(pending);
/**
 * @category Constructors
 */
export const left = eT.left(remote.Pointed);
/**
 * @category Constructors
 */
export const right = eT.right(remote.Pointed);
/**
 * @category Constructors
 */
export const of = right;
/**
 * @category Instances
 */
export const Pointed = {
    URI,
    of,
};
/**
 * @category Constructors
 */
export const zero = remote.zero;
/**
 * @category Instances
 */
export const Zero = {
    URI,
    zero,
};
/**
 * @category Refinements
 */
export const isInitial = remote.isInitial;
/**
 * @category Refinements
 */
export const isPending = remote.isPending;
/**
 * @category Refinements
 */
export const isLeft = remote.exists(e.isLeft);
/**
 * @category Refinements
 */
export const isRight = remote.exists(e.isRight);
/**
 * @category pattern matching
 */
export const fold = (i, p, l, r) => remote.fold(i, p, e.fold(l, r));
/**
 * @category pattern matching
 */
export const foldW = (i, p, l, r) => remote.foldW(i, p, e.foldW(l, r));
/**
 * @internal
 */
const _fold = (fa, onInitial, onPending, onLeft, onRight) => fold(onInitial, onPending, onLeft, onRight)(fa);
/**
 * @category mapping
 */
export const map = eT.map(remote.Functor);
/**
 * @category Instances
 */
export const Functor = {
    URI,
    map: (fa, f) => pipe(fa, map(f)),
};
/**
 * @category mapping
 */
export const bimap = eT.bimap(remote.Functor);
/**
 * @category mapping
 */
export const mapLeft = eT.mapLeft(remote.Functor);
/**
 * @category Instances
 */
export const Bifunctor = {
    URI,
    bimap: (fa, f, g) => pipe(fa, bimap(f, g)),
    mapLeft: (fa, f) => pipe(fa, mapLeft(f)),
};
/**
 * @category utils
 */
export const apW = (fa) => (fab) => {
    if (isLeft(fa))
        return isLeft(fab) ? fab : fa;
    else if (isPending(fa))
        return isLeft(fab) ? fab : fa;
    else if (isInitial(fa))
        return isLeft(fab) || isPending(fab) ? fab : fa;
    else
        return isRight(fab)
            ? right(fab.value.right(fa.value.right))
            : fab;
};
/**
 * @category utils
 */
export const ap = apW;
/**
 * @category Instances
 */
export const Apply = {
    ...Functor,
    ap: (fab, fa) => pipe(fab, ap(fa)),
};
/**
 * @category Instances
 */
export const Applicative = {
    ...Apply,
    ...Pointed,
};
/**
 * @category sequencing
 */
export const chain = (eT.chain(remote.Monad));
/**
 * @category Instances
 */
export const Chain = {
    ...Apply,
    chain: (fa, f) => pipe(fa, chain(f)),
};
/**
 * @category Instances
 */
export const Monad = {
    ...Chain,
    ...Applicative,
};
/**
 * @category folding
 */
export const reduce = (b, f) => (fa) => (isRight(fa) ? f(b, fa.value.right) : b);
/**
 * @category folding
 */
export const reduceRight = (b, f) => (fa) => (isRight(fa) ? f(fa.value.right, b) : b);
/**
 * @category folding
 */
export const foldMap = (M) => (f) => (fa) => isRight(fa) ? f(fa.value.right) : M.empty;
export const Foldable = {
    URI,
    reduce: (fa, b, f) => pipe(fa, reduce(b, f)),
    reduceRight: (fa, b, f) => pipe(fa, reduceRight(b, f)),
    foldMap: (M) => (fa, f) => pipe(fa, foldMap(M)(f)),
};
/**
 * @category traversing
 */
export const traverse = (F) => (fab) => (f) => (isRight(f) ? F.map(fab(f.value.right), right) : F.of(f));
/**
 * @category traversing
 */
export const sequence = (F) => traverse(F)(identity);
/**
 * @category Instances
 */
export const Traversable = {
    ...Functor,
    ...Foldable,
    sequence,
    traverse: (F) => (f, fab) => pipe(f, traverse(F)(fab)),
};
/**
 * @category error handling
 */
export const alt = eT.alt(remote.Monad);
/**
 * @category Instances
 */
export const Alt = {
    ...Functor,
    alt: (fa, that) => pipe(fa, alt(that)),
};
/**
 * @category Instances
 */
export const Alternative = {
    ...Applicative,
    ...Alt,
    ...Zero,
};
/**
 * @category utils
 */
export const extend = (f) => (fa) => isRight(fa) ? right(f(fa)) : fa;
/**
 * @category Instances
 */
export const Extend = {
    ...Functor,
    extend: (wa, f) => pipe(wa, extend(f)),
};
/**
 * @category conversions
 */
export const fromEither = remote.of;
/**
 * @category Instances
 */
export const FromEither = {
    URI,
    fromEither,
};
/**
 * @category utils
 */
export const apFirst = apply.apFirst(Apply);
/**
 * @category utils
 */
export const apSecond = apply.apSecond(Apply);
/**
 * @category do notation
 */
export const apS = apply.apS(Apply);
/**
 * @category do notation
 */
export const bind = chainable.bind(Chain);
/**
 * @category filtering
 */
export const filterOrElse = (pred, onFalse) => (rd) => {
    if (isRight(rd))
        return pred(rd.value.right) ? rd : left(onFalse(rd.value.right));
    return rd;
};
/**
 * @category conversions
 */
export const fromOption = fe.fromOption(FromEither);
/**
 * FIXME This really should be implemented as EitherT.fromNullable(Remote.Pointed), but it's onNull isn't Lazy yet.
 *
 * @category conversions
 */
export const fromNullable = (onNull) => (a) => a != null ? right(a) : left(onNull());
/**
 * @category conversions
 */
export const fromPredicate = fe.fromPredicate(FromEither);
/**
 * @category conversions
 */
export const fromRemote = eT.rightF(remote.Functor);
/**
 * @category lifting
 */
export const fromEitherK = fe.fromEitherK(FromEither);
/**
 * @category lifting
 */
export const fromOptionK = fe.fromOptionK(FromEither);
/**
 * @category lifting
 */
export const fromNullableK = eT.fromNullableK(Pointed);
/**
 * @category lifting
 */
export const fromRemoteK = (f) => flow(f, fromRemote);
/**
 * @category lifting
 */
export const fromRemoteOption = (onNone) => remote.map(e.fromOption(onNone));
/**
 * @category sequencing
 */
export const flattenW = chain(identity);
/**
 * @category sequencing
 */
export const flatten = chain(identity);
/**
 * @category sequencing
 */
export const chainOptionK = (fe.chainOptionK(FromEither, Chain));
/**
 * FIXME This really should be implemented as eT.chainNullableK(remote.Monad), but it's onNull isn't Lazy yet.
 * @category sequencing
 */
export const chainNullableK = (onNull) => (f) => chain(flow(f, fromNullable(onNull)));
/**
 * @category sequencing
 */
export const chainEitherK = (fe.chainEitherK(FromEither, Chain));
/**
 * @category sequencing
 */
export const chainRemoteK = (f) => (fa) => pipe(fa, chain((fromRemoteK(f))));
/**
 * @category sequencing
 */
export const chainFirst = (chainable.chainFirst(Chain));
/**
 * @category sequencing
 */
export const chainFirstEitherK = (fe.chainFirstEitherK(FromEither, Chain));
/**
 * @category sequencing
 */
export const chainFirstOptionK = (onNone) => flow(fromOptionK(onNone), chainFirst);
/**
 * @category sequencing
 */
export const chainFirstRemoteK = (f) => (fa) => pipe(fa, chainFirst((fromRemoteK(f))));
/**
 * @category error handling
 */
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const altValidation = (S) => eT.altValidation(remote.Monad, S);
/**
 * @category error handling
 */
export const orElse = eT.orElse(remote.Monad);
/**
 * @category error handling
 */
export const orElseW = orElse;
/**
 * @category pattern matching
 */
export const match3 = (onNone, onLeft, onRight) => fold(onNone, onNone, onLeft, onRight);
/**
 * @category Destructors
 */
export const getOrElseW = (onNone) => (fa) => (isRight(fa) ? fa.value.right : onNone());
/**
 * @category Destructors
 */
export const getOrElse = (onNone) => (fa) => getOrElseW(onNone)(fa);
/**
 * @category Destructors
 */
export const toNullable = (ma) => isRight(ma) ? ma.value.right : null;
/**
 * @category Destructors
 */
export const toUndefined = (ma) => isRight(ma) ? ma.value.right : undefined;
/**
 * @category Destructors
 */
export const toOption = (fa) => isRight(fa) ? o.some(fa.value.right) : o.none;
/**
 * @category Destructors
 */
export const toEither = (onInitial, onPending) => remote.fold(flow(onInitial, e.left), flow(onPending, e.left), identity);
/**
 * @category utils
 */
export const elem = (E) => (a) => (fa) => isRight(fa) && E.equals(fa.value.right, a);
/**
 * @category utils
 */
export const exists = (predicate) => (fa) => isRight(fa) && predicate(fa.value.right);
/**
 * @category Instances
 */
export const getApplicativeValidation = (SE) => ({
    ...Applicative,
    _E: undefined,
    ap: (fab, fa) => {
        if (isInitial(fa)) {
            return isLeft(fab) ? fab : initial;
        }
        else if (isPending(fa)) {
            return isPending(fab) ? pending : isRight(fab) ? fa : fab;
        }
        else {
            if (e.isLeft(fa.value)) {
                return isLeft(fab) ? left(SE.concat(fab.value.left, fa.value.left)) : fa;
            }
            else {
                return isRight(fab) ? right(fab.value.right(fa.value.right)) : fab;
            }
        }
    },
});
/**
 * TODO tests
 *
 * @category Instances
 */
export const getAltValidation = (SE) => ({
    ...Alt,
    _E: undefined,
    alt: (first, getSecond) => {
        if (isInitial(first)) {
            return getSecond();
        }
        else if (isPending(first)) {
            const second = getSecond();
            return isInitial(second) ? first : second;
        }
        else {
            if (e.isLeft(first.value)) {
                const second = getSecond();
                return isLeft(second)
                    ? left(SE.concat(first.value.left, second.value.left))
                    : isRight(second)
                        ? second
                        : first;
            }
            else {
                return first;
            }
        }
    },
});
/**
 * @category Instances
 */
export const getEq = (EE, EA) => ({
    equals: (x, y) => _fold(x, () => isInitial(y), () => isPending(y), (ex) => isLeft(y) && EE.equals(ex, y.value.left), (ax) => isRight(y) && EA.equals(ax, y.value.right)),
});
/**
 * @category Instances
 */
export const getMonoid = (SA) => remote.getMonoid(e.getSemigroup(SA));
const constLt = constant(-1);
const constEq = constant(0);
const constGt = constant(1);
/**
 * @category Instances
 */
export const getOrd = (OE, OA) => ({
    ...getEq(OE, OA),
    compare: (x, y) => sign(_fold(x, () => _fold(y, constEq, constLt, constLt, constLt), () => _fold(y, constGt, constEq, constLt, constLt), (xLeft) => _fold(y, constGt, constGt, (yLeft) => OE.compare(xLeft, yLeft), constLt), (xRight) => _fold(y, constGt, constGt, constGt, (yRight) => OA.compare(xRight, yRight)))),
});
/**
 * @category Instances
 */
export const getShow = (SE, SA) => ({
    show: fold(() => 'initial', () => 'pending', (e) => `left(${SE.show(e)})`, (a) => `right(${SA.show(a)})`),
});
/**
 * @category traversing
 */
export const sequenceS = apply.sequenceS(Apply);
/**
 * @category traversing
 */
export const sequenceSW = sequenceS;
/**
 * @category traversing
 */
export const sequenceT = apply.sequenceT(Apply);
/**
 * @category traversing
 */
export const sequenceTW = sequenceT;
/**
 * @category traversing
 */
export const sequenceArray = array.sequence(Applicative);
/**
 * @category utils
 */
export const staleWhileRevalidate = (next) => (current) => pipe(next, fold(() => current, () => (isInitial(current) ? next : current), () => next, () => next));
/**
 * @category utils
 */
export const staleIfError = (next) => (current) => pipe(next, fold(() => current, () => (isInitial(current) || isLeft(current) ? next : current), () => (isRight(current) ? current : next), () => next));
//# sourceMappingURL=remote-either.js.map